import {isFeatureEnabled} from 'feature-flags';
import {
  Action,
  Card,
  CardProps,
  closeCurrentDialog,
  openDeleteDialog,
  openDialog,
} from 'platform/components';
import {Box} from 'platform/foundation';

import {useDispatch} from 'react-redux';

import {assoc, isNil} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {omneticApi} from '@dms/api/base';
import {useGetParticipationQuery} from '@dms/api/core';
import {useGetBusinessCaseQuery, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useDeleteCostMutation} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {useSaleVehicleActions} from '../../hooks/useSaleVehicleActions';
import {handleApiError} from '../../utils/handleApiError';
import {refreshDmsDatagrid} from '../../utils/refreshDmsDatagrid';
import {ApplyVehiclePresetForm} from '../ApplyVehiclePresetForm/ApplyVehiclePresetForm';
import {CreateVehicleCostTransactionPreset} from '../CreateVehicleCostTransactionPreset/';
import {CreateVehiclePresetForm} from '../CreateVehiclePresetForm/CreateVehiclePresetForm';
import {DmsDataGrid, type DmsDataGridProps} from '../DmsDataGrid/DmsDataGrid';
import {VehicleAddCostForm} from '../VehicleAddCostForm/VehicleAddCostForm';
import {VehicleEarningCostForm} from '../VehicleEarningCostForm/VehicleEarningCostForm';
import {VehicleEditCostForm} from '../VehicleEditCostForm/VehicleEditCostForm';
import {EditCostForm} from './components/EditCostForm';

interface VehicleCostsGridCardProps extends TestIdProps, Pick<DmsDataGridProps, 'maximization'> {
  vehicleId: string;
  variant?: CardProps['variant'];
  refreshBusinessCase?: () => void;
  isSimplified?: boolean;
  businessCaseId?: string;
  autoHeight?: boolean;
}

const PREDEFINED_COLUMNS = ['name'] as const;
const FIXED_GRID_HEIGHT = 65;

export function VehicleCostsGridCard(props: VehicleCostsGridCardProps) {
  const [deleteCost] = useDeleteCostMutation();
  const {isSaleActionEnabled} = useSaleVehicleActions(props.vehicleId);
  const dispatch = useDispatch();

  const gridCode = props.isSimplified ? 'business_case_costs' : 'vehicle_costs';

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: props.businessCaseId ?? ''},
    {skip: isNilOrEmpty(props.businessCaseId)}
  );

  const invalidateBC = () =>
    dispatch(
      omneticApi.util.invalidateTags([
        'BusinessCaseDetail',
        'BusinessCasePurchasePrices',
        'VehicleCosts',
      ])
    );

  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: props.vehicleId ?? '',
    },
    {skip: isNil(props.vehicleId)}
  );

  const {data: vehicle} = useGetVehicleQuery(
    {vehicleId: props.vehicleId ?? ''},
    {skip: isNil(props.vehicleId)}
  );

  const [canCreateCost, canEditCost, canApplyPresetCost] = usePermissions({
    permissionKeys: ['vehicleCreateCost', 'vehicleEditCost', 'vehicleApplyPresetCost'],
    scopes: {
      vehicleCreateCost: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      vehicleEditCost: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      vehicleApplyPresetCost: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  /**
   * Memo is used because of a bug in DataGrid. See https://carvago.atlassian.net/browse/T20-18662
   */
  const queryModifier = assoc('vehicle', props.vehicleId);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const costId = isArray(rowId) ? rowId[0] : rowId;
    switch (actionKey) {
      case 'delete':
        openDeleteDialog({
          name: getColumnData<string>('name')?.value,
          'data-testid': suffixTestId('deleteDialog', props),
          onConfirm: () =>
            deleteCost({vehicleId: props.vehicleId, costId})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError)
              .then(props.refreshBusinessCase)
              .then(() => invalidateBC())
              .catch((error: {response: Error}) => handleApiError(error.response)),
        });
        break;

      case 'edit':
        if (canEditCost) {
          if (isFeatureEnabled(featureFlags.SALES_COSTS_WITH_VAT)) {
            openDialog(
              <VehicleEditCostForm
                openSimplified={props.isSimplified}
                data-testid={suffixTestId('editDialog', props)}
                costId={costId}
                vehicleId={props.vehicleId}
                onDiscard={closeCurrentDialog}
                onSuccess={() => {
                  refreshData();
                  closeCurrentDialog();
                  invalidateBC();
                  props.refreshBusinessCase?.();
                }}
              />,
              {title: i18n.t('entity.vehicle.costs.editCost'), scrollBehavior: 'outside'}
            );
          } else {
            openDialog(
              <EditCostForm
                data-testid={suffixTestId('editDialog', props)}
                costId={costId}
                vehicleId={props.vehicleId}
                onCancel={closeCurrentDialog}
                onSuccess={() => {
                  refreshData();
                  closeCurrentDialog();
                  invalidateBC();
                  props.refreshBusinessCase?.();
                }}
                isSimplified={props.isSimplified}
                isCostsEditable={isSaleActionEnabled('SALE_VEHICLE_EDIT_EXPECTED_COST')}
              />,
              {title: i18n.t('entity.vehicle.costs.editCost')}
            );
          }
        }
        break;
    }
  };

  const openAddDialog = () =>
    isFeatureEnabled(featureFlags.SALES_COSTS_WITH_VAT)
      ? openDialog(
          <VehicleAddCostForm
            data-testid={suffixTestId('addDialog', props)}
            vehicleId={props.vehicleId}
            onDiscard={closeCurrentDialog}
            openSimplified={props.isSimplified}
            onSuccess={() => {
              closeCurrentDialog();
              invalidateBC();
              props.refreshBusinessCase?.();
              refreshDmsDatagrid(gridCode);
            }}
          />,
          {
            title: i18n.t('entity.vehicle.costs.addCost'),
            scrollBehavior: 'outside',
          }
        )
      : openDialog(
          <VehicleEarningCostForm
            data-testid={suffixTestId('addDialog', props)}
            entity="cost"
            vehicleId={props.vehicleId}
            onCancel={closeCurrentDialog}
            onSuccess={() => {
              closeCurrentDialog();
              invalidateBC();
              props.refreshBusinessCase?.();
              refreshDmsDatagrid(gridCode);
            }}
            businessCaseId={props.businessCaseId}
            isSimplified={props.isSimplified}
            isCostsEditable={isSaleActionEnabled('SALE_VEHICLE_EDIT_EXPECTED_COST')}
          />,
          {
            title: i18n.t('entity.vehicle.costs.addCost'),
            size: props.isSimplified ? 'small' : undefined,
            'data-testid': suffixTestId('addDialog', props),
          }
        );

  const openApplyPresetDialog = () =>
    openDialog(
      <ApplyVehiclePresetForm
        entity="cost"
        vehicleId={props.vehicleId}
        onCancel={closeCurrentDialog}
        onSuccess={() => {
          closeCurrentDialog();
          invalidateBC();
          props.refreshBusinessCase?.();
          refreshDmsDatagrid(gridCode);
        }}
        businessCaseId={props.businessCaseId}
        data-testid={suffixTestId('applyPresetDialog', props)}
      />,
      {title: i18n.t('entity.earningsCosts.actions.addPreset'), size: 'small'}
    );

  const openCreatePresetDialog = () =>
    isFeatureEnabled(featureFlags.SALES_COSTS_WITH_VAT)
      ? openDialog(
          <CreateVehicleCostTransactionPreset
            vehicleId={props.vehicleId}
            onDiscard={closeCurrentDialog}
            onSuccess={closeCurrentDialog}
            data-testid={suffixTestId('createPresetDialog', props)}
          />,
          {title: i18n.t('entity.earningsCosts.actions.savePreset'), size: 'small'}
        )
      : openDialog(
          <CreateVehiclePresetForm
            entity="cost"
            vehicleId={props.vehicleId}
            onCancel={closeCurrentDialog}
            onSuccess={closeCurrentDialog}
            data-testid={suffixTestId('createPresetDialog', props)}
          />,
          {title: i18n.t('entity.earningsCosts.actions.savePreset'), size: 'small'}
        );

  const actions = buildArray<Action>()
    .when(canCreateCost, {
      type: 'button',
      variant: 'link',
      onClick: openCreatePresetDialog,
      leftIcon: 'content/save',
      title: i18n.t('entity.earningsCosts.actions.savePreset'),
      'data-testid': suffixTestId('createPreset', props),
    })
    .when(canApplyPresetCost, {
      type: 'button',
      variant: 'link',
      onClick: openApplyPresetDialog,
      leftIcon: 'content/add_circle',
      title: i18n.t('entity.earningsCosts.actions.addFromPreset'),
      isDisabled: businessCase?.businessCaseState === 'CLOSED',
      'data-testid': suffixTestId('addPresetButton', props),
    })
    .when(canCreateCost, {
      type: 'button',
      variant: 'link',
      onClick: openAddDialog,
      leftIcon: 'content/add_circle',
      title: i18n.t('entity.earningsCosts.actions.addItem'),
      isDisabled: businessCase?.businessCaseState === 'CLOSED',
      'data-testid': suffixTestId('addButton', props),
    });

  return (
    <>
      <Card
        variant={props.variant}
        title={i18n.t('entity.earningsCosts.labels.costs')}
        actions={actions}
      >
        <Box height={props.autoHeight ? undefined : FIXED_GRID_HEIGHT} key={gridCode}>
          <DmsDataGrid
            maximization={props.maximization}
            gridCode={gridCode}
            autoHeight={props.autoHeight}
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
            emptyState={{
              action: canCreateCost
                ? {
                    title: i18n.t('entity.earningsCosts.actions.addCost'),
                    leftIcon: 'content/add_circle',
                    onClick: openAddDialog,
                  }
                : undefined,
            }}
            data-testid={suffixTestId('grid', props)}
          />
        </Box>
      </Card>
    </>
  );
}
